import { useEffect, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown, faChevronRight, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons'
import { Footer, HeaderMiddle, Navbar, TopBar } from '../components/SiteChrome'
import { EbookHeaderCard } from '../components/EbookPromo'
import { WhatsAppChannelSection } from '../components/WhatsAppChannelSection'
import { BlogTagChips } from '../components/BlogTagChips'
import { UpstreamUnavailablePanel } from '../components/UpstreamUnavailablePage'
import { BLOG_FALLBACK_IMAGE, formatPublishedDate, getStorefrontConfig, toExternalImage } from '../lib/dropify'
import type { StorefrontBlogPost } from '../lib/dropify'
import { listBlogPosts } from '../lib/blog'
import { DEFAULT_OG_IMAGE, absoluteUrl } from '../lib/seo'
import { storefrontResponseHeaders } from '../lib/storefrontResponse'

type BlogSearchParams = { q?: string }

export const BLOG_PAGE_SIZE = 12

const FEATURED_POST_IMAGE_TRANSFORM = { width: 1200, height: 700, smart: true, format: 'webp', quality: 78 } as const
const BLOG_CARD_IMAGE_TRANSFORM = { width: 700, height: 420, smart: true, format: 'webp', quality: 75 } as const

export function mergeBlogPostPages(current: StorefrontBlogPost[], incoming: StorefrontBlogPost[]) {
  const seen = new Set(current.map((post) => post.id || post.slug))
  return [...current, ...incoming.filter((post) => !seen.has(post.id || post.slug))]
}

export const Route = createFileRoute('/blog/')({
  validateSearch: (search: Record<string, unknown>): BlogSearchParams => {
    const q = typeof search.q === 'string' && search.q !== '' ? search.q : undefined
    return q === undefined ? {} : { q }
  },
  loaderDeps: ({ search }) => ({ q: search.q ?? '' }),
  loader: async ({ deps }) => {
    const q = deps.q.trim()
    const [config, postsResult] = await Promise.all([
      getStorefrontConfig().catch(() => null),
      listBlogPosts({ data: { q: q || undefined, limit: q ? 50 : BLOG_PAGE_SIZE } })
        .then((posts) => ({ posts, unavailable: false }))
        .catch(() => ({ posts: [] as StorefrontBlogPost[], unavailable: true })),
    ])
    return {
      config,
      posts: postsResult.posts,
      postsUnavailable: postsResult.unavailable,
      q,
    }
  },
  headers: ({ loaderData }) => storefrontResponseHeaders(loaderData?.postsUnavailable ?? false),
  head: () => {
    const title = 'Blog Dropify | Panduan Pembelian Grosir'
    const description = 'Artikel untuk reseller dan pembeli grosir tentang pemilihan produk, satuan pembelian, MOQ, pengadaan, dan data yang perlu dikirim saat meminta quotation.'

    return {
      meta: [
        { title },
        { name: 'description', content: description },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:url', content: absoluteUrl('/blog') },
        { property: 'og:image', content: DEFAULT_OG_IMAGE },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: DEFAULT_OG_IMAGE },
      ],
      links: [
        { rel: 'canonical', href: absoluteUrl('/blog') },
      ],
    }
  },
  component: BlogListPage,
})

function FeaturedPost({ post }: { post: StorefrontBlogPost }) {
  const publishedDate = formatPublishedDate(post.published_at)
  const postHref = `/blog/${post.slug || post.id}`

  return (
    <div className="group block lg:col-span-2 relative rounded-2xl overflow-hidden bg-slate-900" style={{ minHeight: 400 }}>
      <a href={postHref} aria-label={post.title} className="absolute inset-0 z-0" />
      <img
        src={toExternalImage(post.featured_image_url, FEATURED_POST_IMAGE_TRANSFORM) || BLOG_FALLBACK_IMAGE}
        alt={post.featured_image_alt || post.title}
        width="1200"
        height="700"
        className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:scale-105 transition-transform duration-500"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent" />
      <div className="relative h-full flex flex-col justify-end p-8" style={{ minHeight: 400 }}>
        <div className="flex items-center gap-2 mb-3 flex-wrap relative z-10">
          <BlogTagChips tags={post.tags} chipClassName="inline-flex min-h-6 items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white transition-colors" />
          {publishedDate && <span className="text-slate-400 text-xs">{publishedDate}</span>}
        </div>
        <h2 className="relative z-10 text-2xl font-black text-white leading-snug mb-3 group-hover:text-blue-300 transition-colors">
          <a href={postHref} className="hover:underline">{post.title}</a>
        </h2>
        <p className="text-slate-300 text-sm leading-relaxed line-clamp-2 max-w-xl">{post.excerpt || 'Baca tips dan informasi seputar bisnis grosir dari Dropify.'}</p>
        <span className="mt-4 inline-flex items-center gap-1.5 text-blue-400 text-sm font-bold uppercase tracking-wider">
          Baca artikel <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
        </span>
      </div>
    </div>
  )
}

function BlogCard({ post }: { post: StorefrontBlogPost }) {
  const publishedDate = formatPublishedDate(post.published_at)
  const postHref = `/blog/${post.slug || post.id}`

  return (
    <div className="group relative bg-white rounded-xl overflow-hidden border border-slate-100 hover:border-blue-200 hover:shadow-lg transition-all duration-200 flex flex-col">
      <a href={postHref} aria-label={post.title} className="absolute inset-0 z-0" />
      <div className="relative overflow-hidden" style={{ height: 200 }}>
        <img
          src={toExternalImage(post.featured_image_url, BLOG_CARD_IMAGE_TRANSFORM) || BLOG_FALLBACK_IMAGE}
          alt={post.featured_image_alt || post.title}
          width="700"
          height="420"
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        {publishedDate && (
          <div className="absolute top-4 left-4 bg-blue-600 text-white text-center px-2.5 py-2 rounded-lg leading-none">
            <span className="text-xs font-semibold uppercase block">{publishedDate}</span>
          </div>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center gap-2 mb-3 flex-wrap relative z-10">
          <BlogTagChips tags={post.tags} chipClassName="inline-flex min-h-6 items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors" />
        </div>
        <h3 className="relative z-10 text-sm font-bold text-slate-800 leading-snug mb-2 group-hover:text-blue-600 transition-colors line-clamp-2 flex-1">
          <a href={postHref} className="hover:underline">{post.title}</a>
        </h3>
        <p className="text-xs text-slate-500 leading-relaxed line-clamp-2 mb-4">{post.excerpt || 'Baca tips dan informasi seputar bisnis grosir dari Dropify.'}</p>
        <span className="text-xs font-bold text-blue-600 group-hover:text-blue-700 uppercase tracking-wider flex items-center gap-1 transition-colors">
          Baca artikel <FontAwesomeIcon icon={faChevronRight} className="w-3.5 h-3.5" />
        </span>
      </div>
    </div>
  )
}

function BlogListPage() {
  const { posts, config, q, postsUnavailable } = Route.useLoaderData()
  const [archivePosts, setArchivePosts] = useState(posts)
  const [hasMore, setHasMore] = useState(!q && posts.length === BLOG_PAGE_SIZE)
  const [loadingMore, setLoadingMore] = useState(false)
  const [loadMoreError, setLoadMoreError] = useState(false)
  const [featured, ...rest] = q ? posts : archivePosts

  useEffect(() => {
    setArchivePosts(posts)
    setHasMore(!q && posts.length === BLOG_PAGE_SIZE)
    setLoadMoreError(false)
  }, [posts, q])

  async function loadMorePosts() {
    if (loadingMore || !hasMore) return
    setLoadingMore(true)
    setLoadMoreError(false)
    try {
      const nextPosts = await listBlogPosts({ data: { limit: BLOG_PAGE_SIZE, offset: archivePosts.length } })
      setArchivePosts((current) => mergeBlogPostPages(current, nextPosts))
      setHasMore(nextPosts.length === BLOG_PAGE_SIZE)
    } catch {
      setLoadMoreError(true)
    } finally {
      setLoadingMore(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <TopBar config={config} />
      <HeaderMiddle />
      <Navbar />

      <main>
      <div className="bg-slate-900 py-14">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-[1.4fr_1fr]">
            <div>
              <nav className="flex items-center gap-1.5 text-xs text-slate-400 mb-4">
                <a href="/" className="hover:text-white transition-colors">Beranda</a>
                <FontAwesomeIcon icon={faChevronRight} className="w-3 h-3" />
                <span className="text-white font-medium">Blog</span>
              </nav>
              <h1 className="text-4xl font-black text-white uppercase tracking-tight mb-2">Tips Berjualan</h1>
              <p className="text-slate-300 text-sm max-w-lg mb-6">
                Tips bisnis, informasi produk grosir, dan artikel terkini dari Dropify.
              </p>

              <form method="get" action="/blog" role="search" className="relative max-w-xl">
                <FontAwesomeIcon icon={faMagnifyingGlass} className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="search"
                  name="q"
                  defaultValue={q}
                  placeholder="Cari artikel…"
                  aria-label="Cari artikel"
                  className="w-full rounded-xl border border-slate-700 bg-slate-800 py-3 pl-11 pr-28 text-sm text-white placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                />
                <button
                  type="submit"
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-lg bg-blue-600 px-4 py-2 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-blue-700"
                >
                  Cari
                </button>
              </form>
            </div>

            <EbookHeaderCard location="blog_index" />
          </div>
        </div>
      </div>

      <WhatsAppChannelSection />

      <div className="max-w-7xl mx-auto px-4 py-12">
        {postsUnavailable ? (
          <UpstreamUnavailablePanel
            path={q ? `/blog?q=${encodeURIComponent(q)}` : '/blog'}
            title="Daftar artikel sedang disinkronkan"
            description="Struktur blog dan navigasi tetap tersedia. Coba muat ulang atau hubungi admin jika Anda sedang mencari panduan produk tertentu."
          />
        ) : q ? (
          posts.length > 0 ? (
            <>
              <h2 className="text-xl font-black text-slate-900 tracking-tight mb-2">
                {posts.length} hasil untuk "{q}"
              </h2>
              <div className="w-12 h-1 bg-blue-500 rounded mb-8" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {posts.map((post) => (
                  <BlogCard key={post.id} post={post} />
                ))}
              </div>
            </>
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-white px-6 py-16 text-center text-slate-500">
              Tidak ada artikel yang cocok untuk "{q}".{' '}
              <a href="/blog" className="text-blue-600 font-semibold hover:underline">Lihat semua artikel</a>
            </div>
          )
        ) : featured ? (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
              <FeaturedPost post={featured} />
              <div className="flex flex-col gap-6">
                {rest.slice(0, 2).map((post) => (
                  <BlogCard key={post.id} post={post} />
                ))}
              </div>
            </div>

            <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight mb-2">Artikel Lainnya</h2>
            <div className="w-12 h-1 bg-blue-500 rounded mb-8" />
            <div id="blog-archive-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.slice(2).map((post) => (
                <BlogCard key={post.id} post={post} />
              ))}
            </div>
            {(hasMore || loadingMore) && (
              <div className="mt-10 flex justify-center">
                <button
                  type="button"
                  onClick={loadMorePosts}
                  disabled={loadingMore}
                  aria-controls="blog-archive-grid"
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3 text-sm font-bold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:cursor-wait disabled:bg-blue-400"
                >
                  {loadingMore ? 'Memuat artikel...' : 'Muat lebih banyak artikel'}
                  {!loadingMore && <FontAwesomeIcon icon={faChevronDown} className="h-4 w-4" />}
                </button>
              </div>
            )}
            {loadMoreError && (
              <p className="mt-4 text-center text-sm text-rose-700" role="status">
                Artikel berikutnya belum dapat dimuat. Silakan coba lagi.
              </p>
            )}
          </>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white px-6 py-16 text-center text-slate-500">
            Belum ada artikel
          </div>
        )}
      </div>

      </main>

      <Footer config={config} />
    </div>
  )
}
